import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { DA_TO_EN } from '@/components/en/EnLangSwitch';

const SCANDINAVIAN = ['da', 'nb', 'nn', 'no', 'sv', 'se', 'is', 'fo'];
const PREF_KEY = 'enkel_lang_pref';
const REDIRECT_KEY = 'enkel_auto_lang_done';

/**
 * On first load, redirects non-Scandinavian visitors from a Danish page to its
 * English counterpart. Scandinavian browsers (da/no/sv/is/fo) stay in Danish.
 * Runs once per session and respects a manual language choice.
 */
export default function AutoLanguageRedirect() {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    // Only act on Danish pages that have an English equivalent
    const currentPath = location.pathname.replace(/\/$/, '').toLowerCase() || '/';
    const enTarget = DA_TO_EN[currentPath];
    if (!enTarget) return;
    // Merchant links must keep the exact product landing page, not redirect to a catalog.
    if (['/zaptec-go', '/zaptec-go-2', '/nexblue-edge-2'].includes(currentPath)) return;

    // Never auto-redirect bots / Lighthouse / headless crawlers — they must
    // measure and index the canonical Danish page (redirecting them tanks
    // PageSpeed scores and confuses indexing).
    const ua = navigator.userAgent || '';
    if (navigator.webdriver || /Lighthouse|HeadlessChrome|Googlebot|bingbot|Slurp|DuckDuckBot|Baiduspider|YandexBot|PTST|GTmetrix/i.test(ua)) return;

    // Respect manual choice or an already-performed auto-redirect this session
    if (localStorage.getItem(PREF_KEY)) return;
    if (sessionStorage.getItem(REDIRECT_KEY)) return;

    // Honour an explicit ?lang= override
    const params = new URLSearchParams(window.location.search);
    if (params.get('lang')) return;

    const langs = navigator.languages && navigator.languages.length
      ? navigator.languages
      : [navigator.language || ''];
    const primary = (langs[0] || '').toLowerCase().split('-')[0];

    sessionStorage.setItem(REDIRECT_KEY, '1');

    if (primary && !SCANDINAVIAN.includes(primary)) {
      navigate(enTarget + location.search + (location.hash === '#aftaler' ? '#plans' : location.hash), { replace: true });
    }
  }, [location.pathname, location.search, location.hash, navigate]);

  return null;
}